import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Firebase
import { auth } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

// --- Core Components ---
import Header from './components/Header';
import Login from './components/Login';
import SelectionDashboard from './components/SelectionDashboard'; // The new main menu
import CoaHome from './components/CoaHome';                     // The COA section's menu
import HrmDashboard from './components/HrmDashboard';           // The HRM section's main page

// --- COA Feature Components ---
import ProductManagement from './components/ProductManagement';
import CustomerManagement from './components/CustomerManagement';
import CreateCoa from './components/CreateCoa';
import CoaDashboard from './components/CoaDashboard';
import EditCoa from './components/EditCoa';
import AnalyticsDashboard from './components/AnalyticsDashboard';

// --- Admin & Utility Components ---
import AdminCreateUser from './components/AdminCreateUser';
import PasswordProtect from './components/PasswordProtect';

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Firebase listener for authentication state changes (login/logout)
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    // Cleanup listener on component unmount
    return unsubscribe;
  }, []);

  // Check if the current user is the administrator
  const isAdmin = currentUser && currentUser.email === 'kishanvba@gmail.com';

  // Show a loading indicator while Firebase checks for an active session
  if (loading) {
    return <div>Loading Application...</div>;
  }

  return (
    <Router>
      <div className="App">
        {/* The Header is displayed on all pages for logged-in users */}
        {currentUser && <Header />}
        
        <div style={{ padding: '20px' }}>
          <Routes>
            {/* --- Public Route --- */}
            {/* The login page is accessible to everyone */}
            <Route path="/login" element={<Login />} />
            
            {/* --- Main Authenticated Routes --- */}
            {/* After login, the user lands on the selection dashboard */}
            <Route path="/" element={currentUser ? <SelectionDashboard /> : <Navigate to="/login" />} />
            <Route path="/coa-home" element={currentUser ? <CoaHome /> : <Navigate to="/login" />} />
            <Route path="/hrm-home" element={currentUser ? <HrmDashboard /> : <Navigate to="/login" />} />

            {/* --- COA Feature Routes (All require login) --- */}
            <Route path="/products" element={currentUser ? <ProductManagement /> : <Navigate to="/login" />} />
            <Route path="/create-coa" element={currentUser ? <CreateCoa key="coa-create" /> : <Navigate to="/login" />} />
            <Route path="/coa-dashboard" element={currentUser ? <CoaDashboard key="coa-dash" /> : <Navigate to="/login" />} />
            <Route path="/coa/edit/:recordId" element={currentUser ? <EditCoa key="coa-edit" /> : <Navigate to="/login" />} />

            {/* --- Sample COA Routes (pre-shipment / approvals; same components in sample mode) --- */}
            <Route path="/create-sample-coa" element={currentUser ? <CreateCoa key="sample-create" sample /> : <Navigate to="/login" />} />
            <Route path="/sample-coa-dashboard" element={currentUser ? <CoaDashboard key="sample-dash" sample /> : <Navigate to="/login" />} />
            <Route path="/sample-coa/edit/:recordId" element={currentUser ? <EditCoa key="sample-edit" sample /> : <Navigate to="/login" />} />
            <Route path="/analytics" element={currentUser ? <AnalyticsDashboard /> : <Navigate to="/login" />} />
            
            {/* --- Specially Protected Routes --- */}
            <Route 
              path="/customers" 
              element={
                currentUser ? (
                  <PasswordProtect password="omkr">
                    <CustomerManagement />
                  </PasswordProtect>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            
            {/* --- Admin-Only Route --- */}
            {/* This route is only accessible to the admin user, otherwise it redirects */}
            <Route 
              path="/admin/create-user" 
              element={isAdmin ? <AdminCreateUser /> : <Navigate to="/" />} 
            />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;